// =============================================================================
// QR scanning (§2.3). Browser-native camera, no peripheral, no pairing.
//
// Non-negotiable: scanning is the convenience path, never the only path. Every
// screen that uses this ALSO has a text input that works with the camera denied,
// broken, or pointed at a scuffed label. If the scanner fails, the transaction
// must still be completable.
// =============================================================================

// html5-qrcode is ~400 kB and is only needed once someone taps Scan. Loading it
// lazily keeps the initial download small, which matters when the app is opened
// over mobile data at a front desk. The typed-entry fallback (§2.3) works
// whether or not this ever loads.
let Html5Qrcode = null;
async function loadDecoder() {
  if (!Html5Qrcode) ({ Html5Qrcode } = await import('html5-qrcode'));
  return Html5Qrcode;
}

let instance = null;
let running = false;

export const isScanning = () => running;

// Debounce identical reads: the decoder fires many times per second while a
// code is in frame, and each one would otherwise add a duplicate to the basket.
let lastCode = null;
let lastAt = 0;
const REPEAT_MS = 2500;

export async function startScanner(elementId, onCode, onError) {
  if (running) return;
  try {
    const Decoder = await loadDecoder();
    instance = new Decoder(elementId, { verbose: false });
    running = true;
    await instance.start(
      { facingMode: 'environment' },
      {
        fps: 10,
        // A square box sized to the viewport keeps the guide visible on a
        // phone held in one hand without forcing the user to aim precisely.
        qrbox: (w, h) => { const s = Math.floor(Math.min(w, h) * 0.7); return { width: s, height: s }; },
        aspectRatio: 1,
      },
      (text) => {
        const now = Date.now();
        if (text === lastCode && now - lastAt < REPEAT_MS) return;
        lastCode = text; lastAt = now;
        // A short vibration is the fastest possible confirmation for someone
        // who is looking at the book, not the screen.
        if (navigator.vibrate) navigator.vibrate(40);
        onCode(text);
      },
      () => { /* per-frame decode misses are normal; ignore */ },
    );
  } catch (err) {
    running = false;
    instance = null;
    onError?.(err);
  }
}

export async function stopScanner() {
  if (!instance || !running) return;
  running = false;
  try { await instance.stop(); } catch { /* already stopped */ }
  try { instance.clear(); } catch { /* nothing rendered */ }
  instance = null;
  lastCode = null;
}
